import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import ShimmerButton from './ShimmerButton';

interface AccountTypeModalProps {
    isOpen: boolean;
    onClose: (accountType: 'personal' | 'business') => Promise<void> | void;
}

export function AccountTypeModal({ isOpen, onClose }: AccountTypeModalProps) {
    const [selectedType, setSelectedType] = useState<
        'personal' | 'business' | null
    >(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleContinue = async () => {
        if (selectedType && !isLoading) {
            setIsLoading(true);
            try {
                await onClose(selectedType);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 flex items-center justify-center z-50 p-4"
                    >
                        <div className="bg-gray-900/95 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-gray-800">
                            <h2 className="text-2xl font-bold text-white text-center mb-4">
                                Welcome to Niio!
                            </h2>
                            <p className="text-gray-300 text-center mb-8">
                                Please select your account type to personalize
                                your experience
                            </p>

                            <div className="space-y-4 mb-8">
                                {/* Business Account Option - Now First */}
                                <button
                                    onClick={() =>
                                        !isLoading &&
                                        setSelectedType('business')
                                    }
                                    disabled={isLoading}
                                    className={`w-full p-4 rounded-xl border-2 transition-all ${
                                        selectedType === 'business'
                                            ? 'border-purple-500 bg-purple-500/10'
                                            : 'border-gray-700 hover:border-gray-600'
                                    } ${
                                        isLoading
                                            ? 'opacity-50 cursor-not-allowed'
                                            : ''
                                    }`}
                                >
                                    <div className="text-left">
                                        <h3 className="text-lg font-semibold text-white mb-1">
                                            🏢 Business Account
                                        </h3>
                                        <p className="text-sm text-gray-400">
                                            For galleries, organizations, and
                                            businesses
                                        </p>
                                    </div>
                                </button>

                                {/* Personal Account Option - Now Second */}
                                <button
                                    onClick={() =>
                                        !isLoading &&
                                        setSelectedType('personal')
                                    }
                                    disabled={isLoading}
                                    className={`w-full p-4 rounded-xl border-2 transition-all ${
                                        selectedType === 'personal'
                                            ? 'border-purple-500 bg-purple-500/10'
                                            : 'border-gray-700 hover:border-gray-600'
                                    } ${
                                        isLoading
                                            ? 'opacity-50 cursor-not-allowed'
                                            : ''
                                    }`}
                                >
                                    <div className="text-left">
                                        <h3 className="text-lg font-semibold text-white mb-1">
                                            🎨 Personal Account
                                        </h3>
                                        <p className="text-sm text-gray-400">
                                            For art enthusiasts, collectors, and
                                            individuals
                                        </p>
                                    </div>
                                </button>
                            </div>

                            <ShimmerButton
                                onClick={handleContinue}
                                disabled={!selectedType || isLoading}
                                className="w-full"
                            >
                                {isLoading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg
                                            className="animate-spin h-5 w-5"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Setting up...
                                    </span>
                                ) : (
                                    'Continue'
                                )}
                            </ShimmerButton>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
